import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  createModal,
  Banner,
  LoadingIndicator,
  RichTable,
  Tooltip
} from '@thermon-platforms/thermon-ui-core';
import { ConfigureDeviceModal, EditView } from '../manage-devices';
import useDevicesStore from 'store/devices';
import useAlarmsStore from 'store/alarms';
import useAlarmTypesStore from 'store/alarm-types';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import { bootstrapChartData, AlarmsByDevice, AlarmsByType } from 'components/charts';
import { getAllChildDevicesByType } from 'util/device';
import DevicesMap from '../devices-map';
import BridgeTableRow from './bridge-table-row';
import StatusIcon from 'components/status-icon';


function LocationView({device}) {
  const location = useLocation();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [alarmsStore, alarmsStoreActions] = useAlarmsStore();
  const [alarmTypesStore] = useAlarmTypesStore();
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();
  const [detail, setDetail] = useState();
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState();
  const [alarms, setAlarms] = useState([]);
  const [editMode, setEditMode] = useState(location.hash == '#network-settings');

  // Edit mode can be navigated directly to via a hash in the url
  // Let's watch for the location to change and set edit mode
  // appropriately
  useEffect(() => {
    setEditMode(location.hash == '#network-settings');
  }, [location]);

  useEffect(() => {
    alarmsStoreActions.query();
    setLoading(true);

    devicesStoreActions.fetchDetail(device.model.id).then((detail) => {
      setDetail(detail);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // Get alarms for the current location
    const locationAlarms = alarmsStore.alarms
      .filter(alarm => {
        if (!alarm.deviceId) return false;
        const path = devicesStore.shallowDevices.get(alarm.deviceId)?.path;
        if (!path) return false;
        return !!path.find(d => {
          return d == device.model;
        });
      });

    setAlarms(locationAlarms);
    /**
     * Compile alarm data grouped by type:
     * {
     *  AlarmType: 0
     * }
     */
    const alarmTypeData = bootstrapChartData();
    alarmTypeData.datasets[0].label = t`Active Alarms`;
    const alarmsCount = locationAlarms
      .reduce((acc, alarm) => {
        if (!acc[alarm.alarmType]) {
          acc[alarm.alarmType] = 1;
        } else {
          acc[alarm.alarmType]++;
        }
        return acc;
      }, {});

    Object.keys(alarmsCount).forEach(alarmType => {
      const type = alarmTypesStore.types.find(type => type.name == alarmType);
      alarmTypeData.labels.push(type?.displayName || alarmType);
      alarmTypeData.datasets[0].data.push(alarmsCount[alarmType]);
    });

    /**
     * Compile alarm count grouped by panel:
     * {
     *  AlarmType: 0
     * }
     */
    const alarmDevicesData = bootstrapChartData();
    alarmDevicesData.datasets[0].label = t`Active Alarms`;
    alarmDevicesData.labels = getAllChildDevicesByType(device.model, 'Panel').map(panel => panel.id);
    const devicesCount = locationAlarms
      .reduce((acc, alarm) => {
        if (!alarm.deviceId) return acc;
        const path = devicesStore.shallowDevices.get(alarm.deviceId)?.path;
        if (!path) return acc;
        const panel = path.find(device => device.type == 'Panel');
        if (!panel) return acc;

        if (!acc[panel.id]) {
          acc[panel.id] = 1;
        } else {
          acc[panel.id]++;
        }
        return acc;
      }, {});

    Object.keys(devicesCount).forEach(alarmDevice => {
      const index = alarmDevicesData.labels.indexOf(alarmDevice);
      alarmDevicesData.datasets[0].data[index] = devicesCount[alarmDevice];
    });

    setChartData({alarmTypeData, alarmDevicesData});
  }, [alarmsStore]);

  function editSettings() {
    const editModal = createModal(<ConfigureDeviceModal device={detail} />);
    editModal.open()
      .then((res) => {
        // Handle side-effects
        devicesStoreActions.forceUpdate();
      })
      .catch(() => {/* no-op */})
  }

  const columns = [
    {
      name: t`Bridge`,
      sortable: true,
      key: 'name'
    },
    // _(msg`Message Success Rate`),
    t`Firmware Vesion`,
    t`Firmware Updated`,
    t`Panels`,
    t`Actions`
  ];

  if (editMode) return (<EditView device={device} />);

  return (
    <>
    <header>
      <h2>
        {loading ?
          <LoadingIndicator /> :
          <StatusIcon device={device} alarms={alarms} />
        }
        {device.model.name}
      </h2>
      <ul className="device-controls">
        {usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <>
          <li>
            <button
              className="settings"
              onClick={editSettings}
              data-tooltip-content={t`Location Settings`}
              data-tooltip-id="control-tooltip"
            >
              <Trans>Settings</Trans>
            </button>
          </li>
          <li>
            <Link
              to={'#network-settings'}
              className="button network"
              data-tooltip-content={t`Network Settings`}
              data-tooltip-id="control-tooltip"
              onClick={() => setEditMode(true)}
            >
              <Trans>Network Settings</Trans>
            </Link>
          </li>
        </> : <></>}
      </ul>
      <Tooltip id="control-tooltip" place="bottom-end" />
    </header>
    <section>
      {device.model.coordinates.latitude ? <div className="container-wrapper">
        <DevicesMap device={device} />
      </div> : <></>}
      {!alarmsStore.loading && !alarms.length ?
        <Banner status="ok" show={true}>No alarms at <strong>{device.model.name}</strong></Banner>
      : null}
      {alarms.length ? <div className="container-wrapper">
        <div className="chart-container">
          {/* <h3><Trans>Alarms by Type</Trans></h3> */}
          <div className="chart">
            <AlarmsByType loading={!chartData} data={chartData?.alarmTypeData} device={device} />
          </div>
        </div>
        <div className="chart-container">
          {/* <h3><Trans>Alarms per Panel</Trans></h3> */}
          <div className="chart">
            <AlarmsByDevice loading={!chartData} data={chartData?.alarmDevicesData} />
          </div>
        </div>
      </div> : null}
      <RichTable
        columns={columns}
        rows={[...device.model.children]}
        responsive={true}
        items={t`bridges`}
      >
        <BridgeTableRow />
      </RichTable>
    </section>
    </>
  )
}

export default LocationView;
